// MD → HTML-Pipeline mit Type.ai-Fixes.
//
// Pipeline-Schritte:
//   1. Type.ai-spezifische Vorverarbeitung (Text-Ebene) + Meta extrahieren
//   2. remark-parse → AST
//   3. remark-gfm
//   4. remark-smartypants (deutsche Anführungszeichen, wenn aktiviert)
//   5. remark-rehype → HAST
//   6. Pandoc-Bild-Attribute auf <img> kleben
//   7. rehype-stringify → HTML
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkSmartypants from "remark-smartypants";
import remarkDirective from "remark-directive";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";
import { visit } from "unist-util-visit";
/**
 * Marker-Direktiven für manuellen Buchsatz (Druck-PDF/X-3).
 *
 * Inline-Form  (Text-Direktive): `:br`, `:nbsp`, `:nobreak[Dr. Müller]`
 * Block-Leaf:                    `::pagebreak`, `::pagebreak{recto}`,
 *                                `::pagebreak{verso}`, `::pagebreak{blank}`
 * Block-Container:               `:::keep ... :::`, `:::fullpage ... :::`
 *
 * Mapping auf HTML:
 *   pagebreak  → <div class="md2bp-pagebreak" data-type="page|recto|verso|blank"/>
 *   br         → <br/>
 *   nbsp       → " " (non-breaking space, U+00A0)
 *   nobreak    → <span class="md2bp-nobreak">…</span>
 *   keep       → <div class="md2bp-keep">…</div>
 *   fullpage   → <div class="md2bp-fullpage">…</div>
 *
 * Unbekannte Direktiven werden ignoriert (= als plain Text gerendert).
 */
const KNOWN_DIRECTIVES = new Set([
    "pagebreak",
    "br",
    "nbsp",
    "nobreak",
    "keep",
    "fullpage",
]);
// ---------- Type.ai-spezifische Text-Preprocessing ---------------------------
/**
 * Entfernt:
 *  - leere `#`-Header (Type.ai nutzt sie als visuelle Spacer)
 *  - das manuelle „# INHALTSVERZEICHNIS"-Kapitel (wir generieren ToC selbst)
 *  - die doppelte Titelseite + alles davor (wenn ein manuelles ToC erkannt wurde,
 *    ist davor garantiert Type.ai-Frontmatter, das unser Tool selbst layoutet)
 *
 * Extrahiert nebenbei Titel/Untertitel/Autor als Vorschlag.
 */
export function preprocessTypeAi(markdown) {
    let md = markdown;
    const suggestedMeta = {};
    // 1. Leere Header-Zeilen entfernen
    md = md.replace(/^#{1,6}\s*$/gm, "");
    // 2. Manuelles ToC erkennen (Pattern: H1 "INHALTSVERZEICHNIS" o.ä.)
    const tocRegex = /^#\s+(inhaltsverzeichnis|contents|table of contents)\s*$[\s\S]*?(?=^#\s+\S)/im;
    const tocMatch = md.match(tocRegex);
    if (tocMatch && typeof tocMatch.index === "number") {
        // 3. Frontmatter = alles vor dem ToC — extrahieren, dann droppen
        const frontmatter = md.slice(0, tocMatch.index);
        const meta = extractMetaFromFrontmatter(frontmatter);
        Object.assign(suggestedMeta, meta);
        // 4. Frontmatter + ToC droppen
        md = md.slice(tocMatch.index + tocMatch[0].length).trim();
    }
    else {
        // Kein ToC: nur Titel/Autor aus erstem H1/H2/Italic extrahieren, NICHTS droppen
        const meta = extractMetaFromFrontmatter(md.slice(0, 2000));
        if (meta.title)
            suggestedMeta.title = meta.title;
        if (meta.subtitle)
            suggestedMeta.subtitle = meta.subtitle;
        if (meta.author)
            suggestedMeta.author = meta.author;
    }
    // 5. Mehrfache Leerzeilen kondensieren
    md = md.replace(/\n{3,}/g, "\n\n").trim();
    return { markdown: md, suggestedMeta };
}
function extractMetaFromFrontmatter(text) {
    const out = {};
    // Erstes H1
    const h1 = text.match(/^#\s+(.+)$/m);
    if (h1)
        out.title = h1[1].trim();
    // Erstes H2
    const h2 = text.match(/^##\s+(.+)$/m);
    if (h2)
        out.subtitle = h2[1].trim();
    // *italic*-Zeile (oft Autor bei Type.ai: "*von Hecky*")
    const authorLine = text.match(/^\*([^*]{2,80})\*\s*$/m);
    if (authorLine) {
        out.author = authorLine[1].replace(/^(von|by)\s+/i, "").trim();
    }
    return out;
}
// ---------- Bild-Attribute (Pandoc-Style { width=50% .class #id }) ------------
/** Parst den Inhalt eines Pandoc-Attribut-Blocks (ohne die `{}`) in ein Objekt:
 *  `.klasse` → class, `#id` → id, `key=value` → key. */
function parsePandocAttrs(attrPart) {
    const parsed = {};
    const re = /([.#]?[\w-]+)(?:=("[^"]*"|'[^']*'|[^\s}]+))?/g;
    let m;
    while ((m = re.exec(attrPart)) !== null) {
        const key = m[1];
        const val = (m[2] ?? "").replace(/^["']|["']$/g, "");
        if (key.startsWith(".")) {
            parsed.class = (parsed.class ? parsed.class + " " : "") + key.slice(1);
        }
        else if (key.startsWith("#")) {
            parsed.id = key.slice(1);
        }
        else {
            parsed[key] = val;
        }
    }
    return parsed;
}
/** Wandelt eine Width-Angabe (`50%`, `480`, `480px`) in einen sicheren
 *  CSS-`max-width`-Wert um, der nie über die Seitenbreite hinausgeht. */
function widthToMaxWidth(width) {
    const w = width.endsWith("px")
        ? width
        : isFinite(Number(width))
            ? width + "px"
            : width;
    return `max-width: min(${w}, 100%);`;
}
// ---------- Hauptfunktion ----------------------------------------------------
/**
 * Normalisiert Markdown einmal beim Upload:
 *  - entfernt Type.ai-Frontmatter, leere Header, Mehrfach-Newlines
 *  - extrahiert Pandoc-Bild-Attribute (`{ width=80% }`)
 * Liefert den normalisierten Text + die extrahierten Image-Attrs.
 *
 * Wichtig: ab diesem Punkt ist `normalized` identisch mit dem, was wir später
 * parsen. Die Click-to-Edit-Offsets im DOM beziehen sich also auf `normalized` —
 * und der Editor-State (`rawMarkdown`) muss ebenfalls `normalized` sein, sonst
 * landen Marker-Inserts an der falschen Stelle.
 */
export function normalizeMarkdownInput(markdown) {
    const pre = preprocessTypeAi(markdown);
    // Pandoc-Bild-Attribute (`![…](…){width=50%}`) bleiben jetzt IM Text stehen
    // und werden beim Parsen direkt auf das <img> angewandt (siehe parseMarkdown).
    // So überlebt eine pro-Bild-Breite jeden Reparse und das Click-to-Edit kann
    // sie in der Markdown-Quelle ändern. (Früher wurden sie hier rausgeschnitten —
    // dann gingen sie beim ersten Reparse verloren.)
    return {
        normalized: pre.markdown,
        suggestedMeta: pre.suggestedMeta,
        imageAttrs: [],
    };
}
export async function parseMarkdown(markdown, opts) {
    let cleaned;
    let suggestedMeta;
    if (opts.skipNormalize) {
        cleaned = markdown;
        suggestedMeta = {};
    }
    else {
        const norm = normalizeMarkdownInput(markdown);
        cleaned = norm.normalized;
        suggestedMeta = norm.suggestedMeta;
    }
    const wordCount = cleaned
        .replace(/`[^`]*`/g, " ")
        .replace(/[#>*_\-\[\]\(\)!]/g, " ")
        .split(/\s+/)
        .filter(Boolean).length;
    const headings = [];
    const images = [];
    let headingIdx = 0;
    let lastHeadingId;
    const processor = unified().use(remarkParse).use(remarkGfm).use(remarkDirective);
    if (opts.smartQuotes) {
        // retext-smartypants nimmt `openingQuotes` / `closingQuotes` als
        // { double, single }-Objekte. Deutsche Regeln: „..." und ‚...'.
        const quoteOpts = opts.language === "de"
            ? {
                openingQuotes: { double: "„", single: "‚" },
                closingQuotes: { double: "“", single: "‘" },
            }
            : {
                openingQuotes: { double: "“", single: "‘" },
                closingQuotes: { double: "”", single: "’" },
            };
        processor.use(remarkSmartypants, quoteOpts);
    }
    processor
        .use(() => (tree) => {
        // Tree-Walk: Headings UND Images in Dokument-Reihenfolge sammeln, damit
        // jedes Bild dem letzten vorangegangenen Heading zugeordnet werden kann.
        // Zusätzlich:
        //   - Direktiven (leaf/text/container) in HTML-Knoten umsetzen
        //   - mdast-Offset (node.position.start.offset) auf jeden Block kleben,
        //     damit das Click-to-Edit-Layer den passenden Quelltext-Bereich findet
        visit(tree, (node, index, parent) => {
            // Direktiven zuerst — vor der Image/Heading-Zuordnung, damit pagebreak-
            // Marker das nearestHeadingId nicht versauen.
            if (node.type === "leafDirective" ||
                node.type === "containerDirective" ||
                node.type === "textDirective") {
                applyDirective(node);
                return;
            }
            if (node.type === "heading") {
                const text = mdastTextContent(node);
                if (!text.trim())
                    return;
                const id = slugify(text) + "-" + headingIdx;
                const level = Math.min(Math.max(node.depth, 1), 6);
                headings.push({ level, text, id, index: headingIdx });
                node.data = node.data || {};
                node.data.hProperties = {
                    ...(node.data.hProperties || {}),
                    id,
                    ...sourceOffsetAttrs(node),
                };
                headingIdx++;
                lastHeadingId = id;
            }
            else if (node.type === "image") {
                const id = "img-" + images.length;
                // Pandoc-Attribut-Block, der dem Bild direkt folgt (`…){width=50%}`),
                // konsumieren: Werte aufs <img> anwenden und den Literal-Text entfernen,
                // damit `{width=50%}` nicht sichtbar im Buch landet. Der Block steht im
                // Quelltext erhalten (rawMarkdown) — nur der gerenderte Text wird gekürzt.
                let imgAttrs = {};
                if (parent && Array.isArray(parent.children) && typeof index === "number") {
                    const sib = parent.children[index + 1];
                    if (sib && sib.type === "text" && typeof sib.value === "string") {
                        const m = sib.value.match(/^\{([^}]*)\}/);
                        if (m) {
                            imgAttrs = parsePandocAttrs(m[1]);
                            sib.value = sib.value.slice(m[0].length);
                        }
                    }
                }
                images.push({
                    src: node.url || "",
                    alt: node.alt || "",
                    id,
                    nearestHeadingId: lastHeadingId,
                    widthHint: imgAttrs.width,
                });
                node.data = node.data || {};
                const props = {
                    ...(node.data.hProperties || {}),
                    "data-img-id": id,
                    // Quelltext-Position des Bildes → Click-to-Edit (Größe ändern).
                    ...sourceOffsetAttrs(node),
                };
                if (imgAttrs.width)
                    props.style = widthToMaxWidth(imgAttrs.width);
                if (imgAttrs.class)
                    props.className = imgAttrs.class.split(/\s+/);
                if (imgAttrs.id)
                    props.id = imgAttrs.id;
                node.data.hProperties = props;
            }
            else if (node.type === "paragraph" ||
                node.type === "blockquote" ||
                node.type === "list" ||
                node.type === "listItem" ||
                node.type === "thematicBreak" ||
                node.type === "code" ||
                node.type === "table") {
                // Quelltext-Position auf alle Block-Knoten — wird vom Click-to-Edit
                // ausgelesen, um Marker an der richtigen Stelle in der MD-Quelle einzufügen.
                node.data = node.data || {};
                node.data.hProperties = {
                    ...(node.data.hProperties || {}),
                    ...sourceOffsetAttrs(node),
                };
            }
        });
    })
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(() => (tree) => {
        visit(tree, "element", (node) => {
            // Marker-Pillen einfügen: nach remark-rehype haben unsere Marker-divs
            // schon die richtige className + data-type. Wir fügen einen klickbaren
            // Pillen-Span hinein. Im Druck per CSS wieder ausgeblendet.
            const cls = node.properties?.className;
            const classList = Array.isArray(cls)
                ? cls
                : typeof cls === "string"
                    ? cls.split(/\s+/)
                    : [];
            if (classList.includes("md2bp-pagebreak")) {
                const subtype = String(node.properties?.dataType ?? node.properties?.["data-type"] ?? "page");
                node.children = [pillElement("pagebreak", subtype)];
            }
            else if (classList.includes("md2bp-keep")) {
                node.children = [pillElement("keep", ""), ...(node.children || [])];
            }
            else if (classList.includes("md2bp-fullpage")) {
                node.children = [pillElement("fullpage", ""), ...(node.children || [])];
            }
        });
    })
        .use(rehypeStringify, {
        allowDangerousHtml: true,
        // XHTML-kompatible self-closing void elements (<hr/>, <br/>, <img/>) —
        // sonst knallt der EPUB-XML-Parser.
        closeSelfClosing: true,
    });
    const file = await processor.process(cleaned);
    return {
        html: String(file),
        headings,
        images,
        wordCount,
        suggestedMeta,
    };
}
// ---------- Hilfsfunktionen --------------------------------------------------
/**
 * Liefert einen HAST-Span, der den Marker im Preview als klickbare Pille darstellt.
 * Wird vom Stylesheet per Default versteckt; sichtbar nur wenn `body.md2bp-show-markers`
 * gesetzt ist. Im Druck immer unsichtbar.
 *
 *  - kind: "pagebreak" | "keep" | "fullpage"
 *  - subtype: bei pagebreak "page"|"recto"|"verso"|"blank", sonst leer
 */
function pillElement(kind, subtype) {
    const pagebreakLabels = {
        page: "Seitenumbruch",
        recto: "Seitenumbruch → rechte Seite",
        verso: "Seitenumbruch → linke Seite",
        blank: "Leerseite einschieben",
    };
    const label = kind === "pagebreak"
        ? pagebreakLabels[subtype] || pagebreakLabels.page
        : kind === "keep"
            ? "Block zusammenhalten"
            : kind === "fullpage"
                ? "Eigene Seite"
                : kind;
    return {
        type: "element",
        tagName: "span",
        properties: {
            className: ["md2bp-marker-pill", `md2bp-marker-pill-${kind}`],
            "data-marker-kind": kind,
            "data-marker-subtype": subtype,
        },
        children: [{ type: "text", value: label }],
    };
}
function mdastTextContent(node) {
    if (!node)
        return "";
    if (typeof node.value === "string")
        return node.value;
    if (Array.isArray(node.children))
        return node.children.map(mdastTextContent).join("");
    return "";
}
/**
 * Liest Start- und End-Offset der mdast-Node aus und packt sie als data-Attribute.
 * Beide werden vom Click-to-Edit-Layer benutzt: `start` bestimmt die Einfügeposition,
 * `end` ermöglicht das Erkennen, ob ein Marker direkt vor diesem Block schon existiert.
 */
function sourceOffsetAttrs(node) {
    const start = node?.position?.start?.offset;
    const end = node?.position?.end?.offset;
    const out = {};
    if (typeof start === "number")
        out["data-md-start"] = String(start);
    if (typeof end === "number")
        out["data-md-end"] = String(end);
    return out;
}
/**
 * Wandelt eine mdast-Direktive (leaf/text/container) in einen HAST-Knoten um,
 * indem wir hName/hProperties/(ggf.) hChildren setzen. `remark-rehype` baut
 * daraus dann das passende HTML.
 *
 * Unbekannte Direktiven bleiben als Text stehen (kein Crash).
 */
function applyDirective(node) {
    const name = (node?.name || "").toLowerCase();
    if (!KNOWN_DIRECTIVES.has(name)) {
        // Unbekannt — Direktive zu Plain-Text degradieren, indem wir die Node-Typ-Info
        // ignorieren und einfach die Kinder als Text in einen span packen.
        node.data = node.data || {};
        node.data.hName = "span";
        node.data.hProperties = { className: ["md2bp-unknown-directive"] };
        return;
    }
    node.data = node.data || {};
    const attrs = (node.attributes || {});
    switch (name) {
        case "pagebreak": {
            // Block-Leaf: ::pagebreak{recto} / {verso} / {blank} / (ohne) = einfacher Seitenumbruch
            // Akzeptiert sowohl `::pagebreak{recto}` (key ohne value, in mdast als
            // {recto: ""}) als auch `::pagebreak{type=recto}` (explizit).
            let type = (attrs.type || "").toLowerCase();
            if (!type) {
                for (const k of ["recto", "verso", "blank"]) {
                    if (k in attrs) {
                        type = k;
                        break;
                    }
                }
            }
            if (!type)
                type = "page";
            node.data.hName = "div";
            node.data.hProperties = {
                className: ["md2bp-pagebreak"],
                "data-type": type,
                ...sourceOffsetAttrs(node),
            };
            node.children = [];
            break;
        }
        case "br": {
            // Inline: :br → <br/>
            node.data.hName = "br";
            node.data.hProperties = {};
            node.children = [];
            break;
        }
        case "nbsp": {
            // Inline: :nbsp → Text-Knoten mit U+00A0
            node.type = "text";
            node.value = " ";
            node.data.hName = undefined;
            break;
        }
        case "nobreak": {
            node.data.hName = "span";
            node.data.hProperties = { className: ["md2bp-nobreak"] };
            break;
        }
        case "keep": {
            node.data.hName = "div";
            node.data.hProperties = {
                className: ["md2bp-keep"],
                ...sourceOffsetAttrs(node),
            };
            break;
        }
        case "fullpage": {
            node.data.hName = "div";
            node.data.hProperties = {
                className: ["md2bp-fullpage"],
                ...sourceOffsetAttrs(node),
            };
            break;
        }
    }
}
function slugify(text) {
    return text
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
}
